import React, { useEffect, useState } from "react";
import { BellRing, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "./shared";

const MODES = [
  { id: "instant", label: "Instant", hint: "One email per school as soon as a comparison is final." },
  { id: "weekly", label: "Weekly digest", hint: "One summary every Friday at 3 PM ET of new gaps from the past 7 days." },
  { id: "off", label: "Off", hint: "No gap alert emails." },
];

// Each Portfolio member's own gap-alert email preference (instant / weekly / off + threshold).
export const AlertPrefsCard = ({ pid }) => {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!pid) return;
    fetch(`${API}/api/portfolio/${pid}/me/alert-preferences`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null)).then(setP).catch(() => {});
  }, [pid]);
  if (!p || p.is_viewer) return null;
  const save = async (next) => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/portfolio/${pid}/me/alert-preferences`, {
        method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode: next.mode, enabled: next.mode !== "off", threshold: Number(next.threshold) }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Couldn't save");
      setP({ ...p, ...d }); toast.success("Alert preferences saved");
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };
  const mode = p.mode || (p.enabled ? "instant" : "off");
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="alert-prefs-card">
      <div className="flex items-center gap-2">
        <BellRing className="h-4 w-4 text-[#6b3fa0]" />
        <h2 className="text-sm font-semibold text-slate-900">My gap alert emails</h2>
        {saving && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
      </div>
      <p className="text-sm text-slate-600">Email me when a final School vs Portfolio comparison has a pillar gap at or above my threshold.</p>
      <div className="flex flex-wrap items-center gap-4">
        <div role="radiogroup" className="inline-flex rounded-lg border border-slate-300 p-0.5" data-testid="alert-prefs-mode">
          {MODES.map((m) => (
            <button key={m.id} type="button" role="radio" aria-checked={mode === m.id} disabled={saving}
              onClick={() => mode !== m.id && save({ ...p, mode: m.id })} data-testid={`alert-prefs-mode-${m.id}`}
              className={`min-h-[40px] rounded-md px-3 text-sm font-medium transition-colors ${mode === m.id ? "bg-[#6b3fa0] text-white" : "text-slate-700 hover:bg-slate-100"}`}>
              {m.label}
            </button>
          ))}
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-slate-800">
          Threshold
          <select value={p.threshold} disabled={mode === "off" || saving} onChange={(e) => save({ ...p, mode, threshold: e.target.value })}
            data-testid="alert-prefs-threshold" className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm disabled:opacity-50">
            {[5, 10, 15, 20, 25].map((n) => <option key={n} value={n}>{n} points</option>)}
          </select>
        </label>
      </div>
      <p className="text-xs text-slate-500" data-testid="alert-prefs-mode-hint">{MODES.find((m) => m.id === mode)?.hint}</p>
    </div>
  );
};
